import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import containerService from '../../services/ContainerService.js';
import userService from '../../services/UserService.js';
import { Package, ArrowLeft, Eye, Calendar } from "lucide-react";

const AddContainer = () => {
  const [formData, setFormData] = useState({
    client_id: '',
    container_no: '',
    container_type: '',
    manufacturer: '',
    manufacture_date: '',
    iso_type: '',
    imo_class: '',
    un_number: '',
    serial_number: '',
    size: '',
    radius: '',
    maximum_gross_weight: '',
    tare_weight: '',
    capacity_ltr: '',
    mawp: '',
    test_pressure: '',
    csc: '',
    csc_validity: '',
    cleaning_corporation: '',
    depot_name: '',
    top_discharge: false,
    syphon_tube: false,
    steam_tube_wp: '',
    steam_test_pressure: '',
    shell_material: '',
    shell_thikness: '',
    head_material: '',
    head_thikness: '',
    led: '',
    ned: '',
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean up empty fields
      const cleanedData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          cleanedData[key] = formData[key];
        }
      });

      await containerService.create(cleanedData);
      Alert.success('Container created successfully!');
      setTimeout(() => navigate('/containers'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create container');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Package className="w-5 h-5 text-buttonBg" /> Add New Container
                    </h1>
                  </div>
                  <button
                          onClick={() => navigate('/containers')}
                          className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <ArrowLeft
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Back to Container</span>
                        </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Client <span className="text-danger font-bold">*</span></label>
                        <select
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Client</option>
                          {clients.map((client) => (
                            <option key={client.user_id} value={client.user_id}>
                              {client.name} {client.organization_name ? `(${client.organization_name})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Container No</label>
                        <input
                          type="text"
                          name="container_no"
                          value={formData.container_no}
                          onChange={handleChange}
                          placeholder="Enter container number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Container Type</label>
                        <input
                          type="text"
                          name="container_type"
                          value={formData.container_type}
                          onChange={handleChange}
                          placeholder="Enter container type"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Manufacturer</label>
                        <input
                          type="text"
                          name="manufacturer"
                          value={formData.manufacturer}
                          onChange={handleChange}
                          placeholder="Enter manufacturer"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Manufacture Date</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="manufacture_date"
                            value={formData.manufacture_date}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Serial Number</label>
                        <input
                          type="text"
                          name="serial_number"
                          value={formData.serial_number}
                          onChange={handleChange}
                          placeholder="Enter serial number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Size (Feet)</label>
                        <input
                          type="text"
                          name="size"
                          value={formData.size}
                          onChange={handleChange}
                          placeholder="Enter size"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Radius</label>
                        <input
                          type="text"
                          name="radius"
                          value={formData.radius}
                          onChange={handleChange}
                          placeholder="Enter radius"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Capacity (Ltr)</label>
                        <input
                          type="text"
                          name="capacity_ltr"
                          value={formData.capacity_ltr}
                          onChange={handleChange}
                          placeholder="Enter capacity"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Classification & Identification */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Classification & Identification</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">ISO Type</label>
                        <input
                          type="text"
                          name="iso_type"
                          value={formData.iso_type}
                          onChange={handleChange}
                          placeholder="Enter ISO type"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">IMO Class</label>
                        <input
                          type="text"
                          name="imo_class"
                          value={formData.imo_class}
                          onChange={handleChange}
                          placeholder="Enter IMO class"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">UN Number</label>
                        <input
                          type="text"
                          name="un_number"
                          value={formData.un_number}
                          onChange={handleChange}
                          placeholder="Enter UN number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CSC</label>
                        <input
                          type="text"
                          name="csc"
                          value={formData.csc}
                          onChange={handleChange}
                          placeholder="Enter CSC"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CSC Validity</label>
                        <input
                          type="text"
                          name="csc_validity"
                          value={formData.csc_validity}
                          onChange={handleChange}
                          placeholder="Enter CSC validity"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Weight & Pressure */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Weight & Pressure</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Maximum Gross Weight (kg)</label>
                        <input
                          type="text"
                          name="maximum_gross_weight"
                          value={formData.maximum_gross_weight}
                          onChange={handleChange}
                          placeholder="Enter max gross weight"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tare Weight (kg)</label>
                        <input
                          type="text"
                          name="tare_weight"
                          value={formData.tare_weight}
                          onChange={handleChange}
                          placeholder="Enter tare weight"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">MAWP (Bar)</label>
                        <input
                          type="text"
                          name="mawp"
                          value={formData.mawp}
                          onChange={handleChange}
                          placeholder="Enter MAWP"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Test Pressure (Bar)</label>
                        <input
                          type="text"
                          name="test_pressure"
                          value={formData.test_pressure}
                          onChange={handleChange}
                          placeholder="Enter test pressure"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Location & Service */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Location & Service</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Cleaning Corporation</label>
                        <input
                          type="text"
                          name="cleaning_corporation"
                          value={formData.cleaning_corporation}
                          onChange={handleChange}
                          placeholder="Enter cleaning corporation"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Depot Name</label>
                        <input
                          type="text"
                          name="depot_name"
                          value={formData.depot_name}
                          onChange={handleChange}
                          placeholder="Enter depot name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">LED</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="led"
                            value={formData.led}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">NED</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="ned"
                            value={formData.ned}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 5: Discharge & Steam */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Discharge & Steam</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="top_discharge"
                          name="top_discharge"
                          checked={formData.top_discharge}
                          onChange={handleChange}
                          className="text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                        />
                        <label htmlFor="top_discharge" className="text-xs sm:text-sm font-medium text-secondary">
                          Top Discharge
                        </label>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="syphon_tube"
                          name="syphon_tube"
                          checked={formData.syphon_tube}
                          onChange={handleChange}
                          className="text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                        />
                        <label htmlFor="syphon_tube" className="text-xs sm:text-sm font-medium text-secondary">
                          Syphon Tube
                        </label>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Steam Tube WP (Bar)</label>
                        <input
                          type="text"
                          name="steam_tube_wp"
                          value={formData.steam_tube_wp}
                          onChange={handleChange}
                          placeholder="Enter steam tube WP"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Steam Test Pressure (Bar)</label>
                        <input
                          type="text"
                          name="steam_test_pressure"
                          value={formData.steam_test_pressure}
                          onChange={handleChange}
                          placeholder="Enter steam test pressure"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 6: Material Specifications */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Material Specifications</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Shell Material</label>
                        <input
                          type="text"
                          name="shell_material"
                          value={formData.shell_material}
                          onChange={handleChange}
                          placeholder="Enter shell material"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Shell Thickness (mm)</label>
                        <input
                          type="text"
                          name="shell_thikness"
                          value={formData.shell_thikness}
                          onChange={handleChange}
                          placeholder="Enter shell thickness"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Head Material</label>
                        <input
                          type="text"
                          name="head_material"
                          value={formData.head_material}
                          onChange={handleChange}
                          placeholder="Enter head material"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Head Thickness (mm)</label>
                        <input
                          type="text"
                          name="head_thikness"
                          value={formData.head_thikness}
                          onChange={handleChange}
                          placeholder="Enter head thickness"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON 
                          <Eye
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}

                          <span className="relative z-10">{loading ? 'Creating...' : 'Create Container'}</span>
                      
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/containers')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
                      {/* ICON 
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContainer;